import {
  NormalizedAlert,
  NWSFeature,
  NWSAlertsResponse,
  AlertResponseMetadata,
  AlertSeverity,
  AlertUrgency,
  AlertCertainty,
  AlertStatus,
  AlertMessageType,
  AlertCategory,
} from '../types/alert';
import { APP_CONFIG } from '../config/appConfig';
import { AlertCache } from './alertCache';

// Official 50 US States + DC + PR + Marine/Territory zones recognized by weather.gov
export const VALID_US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
  'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'PR', 'VI', 'GU', 'AS', 'MP'
]);

export class NWSAdapter {
  private static userAgent = APP_CONFIG.nws.userAgent;

  /**
   * Derive an approximate centroid [lat, lon] from GeoJSON coordinates if present
   */
  static deriveCentroid(geometry: any): [number, number] | null {
    if (!geometry || !geometry.coordinates) return null;

    try {
      if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
        // GeoJSON is [lon, lat]
        return [geometry.coordinates[1], geometry.coordinates[0]];
      }

      if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates[0])) {
        const ring = geometry.coordinates[0];
        let sumLat = 0;
        let sumLon = 0;
        let count = 0;
        for (const coord of ring) {
          if (Array.isArray(coord) && coord.length >= 2) {
            sumLon += coord[0];
            sumLat += coord[1];
            count++;
          }
        }
        if (count > 0) {
          return [sumLat / count, sumLon / count];
        }
      }

      if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates[0]?.[0])) {
        const ring = geometry.coordinates[0][0];
        let sumLat = 0;
        let sumLon = 0;
        let count = 0;
        for (const coord of ring) {
          if (Array.isArray(coord) && coord.length >= 2) {
            sumLon += coord[0];
            sumLat += coord[1];
            count++;
          }
        }
        if (count > 0) {
          return [sumLat / count, sumLon / count];
        }
      }
    } catch {
      return null;
    }

    return null;
  }

  /**
   * Classify official event text into granular disruption categories
   */
  static classifyDisruptionType(eventName: string = '', category: string = ''): import('../types/alert').DisruptionType {
    const e = eventName.toLowerCase();

    // Tropical / Cyclones
    if (e.includes('hurricane') || e.includes('tropical') || e.includes('typhoon') || e.includes('storm surge')) {
      return 'Tropical';
    }

    // Severe convective storms & tornadoes
    if (
      e.includes('tornado') ||
      e.includes('severe thunderstorm') ||
      e.includes('hail') ||
      e.includes('high wind') ||
      e.includes('derecho')
    ) {
      return 'Severe';
    }

    // Flooding & hydrologic
    if (
      e.includes('flood') ||
      e.includes('flash flood') ||
      e.includes('hydrologic') ||
      e.includes('inundation')
    ) {
      return 'Flood';
    }

    // Wildfire & extreme fire weather
    if (e.includes('fire') || e.includes('red flag') || category === 'Fire') {
      return 'Wildfire';
    }

    // Winter & freeze
    if (
      e.includes('winter') ||
      e.includes('blizzard') ||
      e.includes('ice') ||
      e.includes('freeze') ||
      e.includes('frost') ||
      e.includes('snow') ||
      e.includes('sleet') ||
      e.includes('avalanche')
    ) {
      return 'Winter';
    }

    // Marine & coastal hazards
    if (
      e.includes('gale') ||
      e.includes('surf') ||
      e.includes('rip current') ||
      e.includes('small craft') ||
      e.includes('hazardous seas') ||
      e.includes('marine')
    ) {
      return 'Marine';
    }

    // General emergency / safety / civil
    if (
      category === 'Safety' ||
      category === 'Security' ||
      category === 'Rescue' ||
      category === 'CBRNE' ||
      e.includes('civil') ||
      e.includes('shelter')
    ) {
      return 'Emergency';
    }

    return 'Weather';
  }

  /**
   * Normalize an incoming NWS Feature into our internal NormalizedAlert model.
   * Keeps official source texts untouched without fabrication.
   */
  static normalizeFeature(feature: NWSFeature): NormalizedAlert | null {
    if (!feature || !feature.properties) return null;

    const p = feature.properties;
    const stableId = p.id || p['@id'] || feature.id;
    if (!stableId) return null;

    const centroid = this.deriveCentroid(feature.geometry);

    // Map severity cleanly with fallback
    const validSeverities: AlertSeverity[] = ['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'];
    const severity: AlertSeverity = validSeverities.includes(p.severity) ? p.severity : 'Unknown';

    // Map urgency
    const validUrgencies: AlertUrgency[] = ['Immediate', 'Expected', 'Future', 'Past', 'Unknown'];
    const urgency: AlertUrgency = validUrgencies.includes(p.urgency) ? p.urgency : 'Unknown';

    // Map certainty
    const validCertainties: AlertCertainty[] = ['Observed', 'Likely', 'Possible', 'Unlikely', 'Unknown'];
    const certainty: AlertCertainty = validCertainties.includes(p.certainty) ? p.certainty : 'Unknown';

    const officialUrl = p['@id'] || `${APP_CONFIG.nws.baseUrl}/alerts/${stableId}`;
    const category = (p.category as AlertCategory) || 'Met';
    const disruptionType = this.classifyDisruptionType(p.event, category);

    return {
      id: stableId,
      event: p.event || 'Special Weather Statement',
      headline: p.headline || p.event || 'Active Weather Alert',
      description: p.description || '',
      instruction: p.instruction || '',
      severity,
      urgency,
      certainty,
      senderName: p.senderName || p.sender || 'National Weather Service',
      areaDesc: p.areaDesc || 'Affected areas not specified by source',
      geometry: feature.geometry || null,
      centroid,
      effective: p.effective || p.sent || new Date().toISOString(),
      onset: p.onset || null,
      expires: p.expires || p.ends || '',
      ends: p.ends || null,
      status: (p.status as AlertStatus) || 'Actual',
      messageType: (p.messageType as AlertMessageType) || 'Alert',
      category,
      sourceAgency: 'NWS',
      country: 'US',
      disruptionType,
      sourceUrl: officialUrl,
      firstSeen: p.sent || p.effective || new Date().toISOString(),
      lastSeen: new Date().toISOString(),
    };
  }

  /**
   * Fetch active alerts either nationally or filtered by state code.
   * Handles caching, timeout, retry, and deduplication.
   */
  static async fetchActiveAlerts(options?: {
    stateCode?: string;
    forceRefresh?: boolean;
  }): Promise<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }> {
    const stateCode = options?.stateCode?.toUpperCase();
    const cacheKey = stateCode ? `alerts_state_${stateCode}` : 'alerts_national';

    // Check cache first unless forced
    if (!options?.forceRefresh) {
      const cached = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (cached && !cached.isStale) {
        return {
          alerts: cached.data.alerts,
          metadata: {
            ...cached.data.metadata,
            isCached: true,
            cacheAgeSeconds: cached.ageSeconds,
          },
        };
      }
    }

    // If a stateCode is passed, verify it is a valid US State/Territory code for NWS.
    // If not a US code (e.g. Canadian province or UK region mistakenly passed), return empty without error.
    if (stateCode && !VALID_US_STATE_CODES.has(stateCode)) {
      return {
        alerts: [],
        metadata: {
          source: 'US National Weather Service (weather.gov)',
          totalAlerts: 0,
          lastChecked: new Date().toISOString(),
          isCached: false,
          cacheAgeSeconds: 0,
          statusMessage: `Area code "${stateCode}" is not a recognized US National Weather Service state.`,
        },
      };
    }

    // Build URL
    let url = `${APP_CONFIG.nws.baseUrl}${APP_CONFIG.nws.alertsEndpoint}`;
    if (stateCode && VALID_US_STATE_CODES.has(stateCode)) {
      url += `?area=${encodeURIComponent(stateCode)}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), APP_CONFIG.nws.requestTimeoutMs);

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/geo+json, application/json',
          'User-Agent': this.userAgent,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`NWS API responded with HTTP ${response.status}: ${response.statusText}`);
      }

      const json: NWSAlertsResponse = await response.json();

      if (!json || !Array.isArray(json.features)) {
        throw new Error('NWS API response was malformed: missing features collection');
      }

      // Deduplicate by stable ID and normalize
      const seenIds = new Set<string>();
      const normalizedAlerts: NormalizedAlert[] = [];

      for (const feature of json.features) {
        const alert = this.normalizeFeature(feature);
        if (alert && !seenIds.has(alert.id)) {
          seenIds.add(alert.id);
          normalizedAlerts.push(alert);
        }
      }

      const metadata: AlertResponseMetadata = {
        source: 'US National Weather Service (weather.gov)',
        totalAlerts: normalizedAlerts.length,
        lastChecked: new Date().toISOString(),
        isCached: false,
        cacheAgeSeconds: 0,
        statusMessage: 'Verified live response',
      };

      // Store in cache
      AlertCache.set(cacheKey, { alerts: normalizedAlerts, metadata });

      return { alerts: normalizedAlerts, metadata };
    } catch (err: any) {
      clearTimeout(timeoutId);

      // On failure, check if we have any stale cached data to gracefully fall back on
      const fallbackCached = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (fallbackCached && fallbackCached.data?.alerts) {
        return {
          alerts: fallbackCached.data.alerts.map(a => ({ ...a, isStale: true })),
          metadata: {
            ...fallbackCached.data.metadata,
            isCached: true,
            isFallback: true,
            cacheAgeSeconds: fallbackCached.ageSeconds,
            statusMessage: `NWS Service warning: ${err.message || 'Connection failed'}. Showing cached data from ${fallbackCached.ageSeconds}s ago.`,
          },
        };
      }

      // No cache available, throw typed error
      throw new Error(`Unable to reach National Weather Service: ${err.message || 'Network timeout'}. Please retry shortly.`);
    }
  }

  /**
   * Fetch single alert details by stable alert ID
   */
  static async fetchAlertById(alertId: string): Promise<NormalizedAlert | null> {
    const cacheKey = `alert_single_${alertId}`;
    const cached = AlertCache.get<NormalizedAlert>(cacheKey);
    if (cached && !cached.isStale) {
      return cached.data;
    }

    const url = `${APP_CONFIG.nws.baseUrl}/alerts/${encodeURIComponent(alertId)}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), APP_CONFIG.nws.requestTimeoutMs);

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/geo+json, application/json',
          'User-Agent': this.userAgent,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`HTTP ${response.status}`);
      }

      const feature: NWSFeature = await response.json();
      const normalized = this.normalizeFeature(feature);
      if (normalized) {
        AlertCache.set(cacheKey, normalized);
      }
      return normalized;
    } catch (err) {
      clearTimeout(timeoutId);
      // Fallback to cache if available
      if (cached) return cached.data;
      return null;
    }
  }
}

